/**
 * RootCause Integration — SDK bridge, UI feedback, error handling
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useRootCauseStore } from '@/store/root-cause.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { ApiError } from '@repo/sdk';

export class RootCauseIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getTenantId() {
        return useTenantStore.getState().selectedTenantId;
    }

    _getErrorMessage(error) {
        if (error instanceof ApiError) {
            const blocked = error.details?.blocked;
            if (Array.isArray(blocked) && blocked.length > 0) {
                const reasons = [...new Set(blocked.map((b) => b.reason).filter(Boolean))];
                if (reasons.length) {
                    return `${getIntegrationErrorMessage(error)} (${reasons.join('; ')})`;
                }
            }
        }
        return getIntegrationErrorMessage(error, {
            notFoundKey: 'generalSystemRootCause:messages.notFoundError',
        });
    }

    async fetchRootCauses(page = 1, pageSize = 10, search = '', sortBy = 'name', sortOrder = 'asc', columnFilters = {}) {
        const store = useRootCauseStore.getState();
        const tenantId = this._getTenantId();

        if (!tenantId) {
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        store.setLoading(true);
        store.setError(null);

        try {
            const sortField = sortBy === 'rootCauseName' ? 'name' : sortBy;

            const filterQuery = Object.entries(columnFilters).reduce((acc, [key, value]) => {
                if (value && value.length > 0) {
                    acc[`filter_${key}`] = value;
                }
                return acc;
            }, {});

            const response = await this.service.getAll({
                page,
                limit: pageSize,
                search,
                sortBy: sortField,
                sortOrder,
                ...filterQuery,
            });

            const { data, pagination, uniqueValues } = response;
            store.setRootCauses(data || []);

            if (pagination) {
                store.setPagination({
                    currentPage: pagination.page || page,
                    itemsPerPage: pagination.pageSize || pageSize,
                    totalItems: pagination.total || 0,
                    totalPages: pagination.totalPages || 0,
                    hasNextPage: (pagination.page < pagination.totalPages),
                    hasPreviousPage: (pagination.page > 1)
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true, data: data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async createRootCause(data) {
        const store = useRootCauseStore.getState();
        store.setLoading(true);

        try {
            const scopeType = data.scopeType || 'tenant';
            const payload = {
                name: data.name,
                description: data.description ?? '',
                scopeType,
                scopeUnitIds: scopeType === 'units' ? (data.scopeUnitIds || []) : [],
            };
            if (data.customFields != null) {
                payload.customFields = data.customFields;
            }

            const response = await this.service.create(payload);
            toast.success(i18next.t('generalSystemRootCause:messages.createSuccess', {
                defaultValue: 'Root cause created',
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateRootCause(id, data) {
        const store = useRootCauseStore.getState();
        store.setLoading(true);

        try {
            const scopeType = data.scopeType ?? 'tenant';
            const payload = {
                name: data.name,
                description: data.description ?? '',
                scopeType,
                scopeUnitIds: scopeType === 'units' ? (data.scopeUnitIds || []) : [],
            };
            if (data.customFields != null) {
                payload.customFields = data.customFields;
            }

            const response = await this.service.update(id, payload);
            toast.success(i18next.t('generalSystemRootCause:messages.updateSuccess', {
                defaultValue: 'Root cause updated',
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteRootCause(id) {
        const store = useRootCauseStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('generalSystemRootCause:messages.deleteSuccess', {
                defaultValue: 'Root cause deleted',
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDeleteRootCauses(ids) {
        const store = useRootCauseStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.bulkDelete(ids);
            const payload = response.data ?? response;
            const blockedCount = payload?.blockedCount ?? 0;
            const deletedCount = payload?.deletedCount;

            toast.success(
                blockedCount > 0
                    ? i18next.t('generalSystemRootCause:messages.bulkDeletePartialSuccess', {
                        deletedCount,
                        blockedCount,
                        defaultValue: '{{deletedCount}} deleted, {{blockedCount}} blocked',
                    })
                    : i18next.t('generalSystemRootCause:messages.bulkDeleteSuccess', {
                        defaultValue: 'Root causes deleted',
                    }),
            );

            return {
                success: true,
                data: payload,
                deletedCount,
                blockedCount,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkUpdateScope(ids, scopeType, scopeUnitIds = []) {
        const store = useRootCauseStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.batchScope({
                ids,
                scopeType,
                scopeUnitIds,
            });
            toast.success(i18next.t('generalSystemRootCause:messages.scopeUpdateSuccess', {
                defaultValue: 'Scope updated',
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
